import { useAuthStore } from '@/features/auth';
import { useDepartment } from './use-departments';
import type { UserRole } from '@coi-live/shared-types';

const ROLE_HIERARCHY: UserRole[] = [
  'observer',
  'staff',
  'manager',
  'department_head',
  'primary_admin',
  'system_owner',
];

function getRoleLevel(role: string | undefined): number {
  if (!role) return -1;
  const index = ROLE_HIERARCHY.indexOf(role as UserRole);
  return index >= 0 ? index : -1;
}

/**
 * Hook for checking user permissions based on role hierarchy.
 */
export function usePermissions() {
  const user = useAuthStore((s) => s.user);
  const userRole = user?.role;
  // Fetch the user's department to derive department-type-aware permissions.
  // Used for `canAssignRiskRequirement` (Risk Management department only).
  const { data: department } = useDepartment(user?.departmentId);
  const isRiskManagement = department?.type === 'risk_management';

  const hasMinRole = (minRole: UserRole): boolean => {
    return getRoleLevel(userRole) >= getRoleLevel(minRole);
  };

  // Risk requirement assignment is restricted to Risk Management dept members.
  // Admins (`primary_admin`, `system_owner`) bypass — they can correct anything.
  const canAssignRiskRequirement = hasMinRole('primary_admin') || (hasMinRole('staff') && isRiskManagement);

  return {
    userRole,
    hasMinRole,
    // View-only check: observer role can only read, not create/edit/delete
    isViewOnly: !hasMinRole('staff'),
    canCreate: hasMinRole('staff'),
    canEdit: hasMinRole('staff'),
    canDelete: hasMinRole('manager'),
    canManageDepartments: hasMinRole('primary_admin'),
    canManageUsers: hasMinRole('primary_admin'),
    canManageTasks: hasMinRole('staff'),
    canAssignTasks: hasMinRole('manager'),
    canTransitionWorkflow: hasMinRole('department_head'),
    canAssignRiskRequirement,
    isRiskManagement,
    isAdmin: hasMinRole('primary_admin'),
    isSystemOwner: hasMinRole('system_owner'),
  };
}
